import { useMemo } from 'react'
import { ActionButton, ButtonRow, Stat, Stats } from '@/components/Form/Form'
import { Paper, Section } from '@/components/Layout/Layout'
import { useCopy } from '@/i18n/useCopy'
import { summarise, useDiagnosticsStore } from '@/store/diagnosticsStore'
import { PATIENCE_MS, useSettingsStore } from '@/store/settingsStore'

/**
 * The app's own report card, in plain numbers.
 *
 * This exists because a voice product fails quietly. Nobody files a bug that
 * says "it usually cuts me off about a fifth of the time"; they just stop
 * using it. Counting the retries, the fallbacks and the reply times turns that
 * into something visible, both to the person using it and to the tuner that
 * suggests a longer listening window when the numbers say so.
 *
 * None of it leaves the device, which is stated on the screen rather than
 * buried in a policy.
 */
export function DiagnosticsScreen() {
  const copy = useCopy()
  const clear = useDiagnosticsStore((store) => store.clear)
  const patience = useSettingsStore((store) => store.patience)

  // Selected as raw fields and derived here, never as `store.summary()`.
  // A selector that builds an object returns a new reference every render,
  // which React reads as a changed snapshot and loops on until the screen
  // renders nothing at all.
  const records = useDiagnosticsStore((store) => store.records)
  const typedFallbacks = useDiagnosticsStore((store) => store.typedFallbacks)
  const interruptions = useDiagnosticsStore((store) => store.interruptions)
  const rejectedTransitions = useDiagnosticsStore((store) => store.rejectedTransitions)

  const summary = useMemo(
    () => summarise({ records, typedFallbacks, interruptions, rejectedTransitions }),
    [records, typedFallbacks, interruptions, rejectedTransitions],
  )

  return (
    <Paper title={copy.ui.diagnostics.title} backTo="/settings">
      {summary.turns === 0 ? (
        <Section>
          <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)' }}>
            {copy.ui.diagnostics.empty}
          </p>
        </Section>
      ) : (
        <>
          <Section>
            <Stats>
              <Stat value={String(summary.turns)} label={copy.ui.diagnostics.turns} />
              <Stat
                value={`${(summary.medianListenMs / 1000).toFixed(1)}s`}
                label={copy.ui.diagnostics.medianLatency}
              />
              <Stat value={String(summary.retries)} label={copy.ui.diagnostics.retries} />
              <Stat
                value={String(summary.interruptions)}
                label={copy.ui.diagnostics.interruptions}
              />
              <Stat
                value={`${(PATIENCE_MS[patience] / 1000).toFixed(1)}s`}
                label={copy.ui.diagnostics.silenceWindow}
              />
            </Stats>
          </Section>

          <Section>
            <ButtonRow>
              <ActionButton variant="danger" onClick={clear}>
                {copy.ui.diagnostics.reset}
              </ActionButton>
            </ButtonRow>
          </Section>
        </>
      )}
    </Paper>
  )
}
